import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, Send, Link2, ExternalLink, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, fecha, hoy, Badge } from './ui';
import { ESTADO_PROPUESTA } from './Propuestas';

const vacio = { intro: '', objetivos: [], items: [], incluye: [], no_incluye: [], plan_pago: [], plazo: '', condiciones: '', email_contacto: '' };

/* Lista de textos simples (objetivos, incluye, no incluye) */
function Lista({ valores, onChange, placeholder }) {
  const set = (i, v) => onChange(valores.map((x, j) => (j === i ? v : x)));
  return (
    <div>
      {valores.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input value={v} onChange={e => set(i, e.target.value)} placeholder={placeholder} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(valores.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...valores, ''])}><Plus size={14} /> Agregar</button>
    </div>
  );
}

export default function PropuestaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [d, setD] = useState(vacio);
  const [clientes, setClientes] = useState([]);
  const [brief, setBrief] = useState('');
  const [redactando, setRedactando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    api.get('/agency/propuestas/' + id).then(r => {
      setP(r.data);
      setD({ ...vacio, ...(r.data.datos || {}) });
    }).catch(() => toast.error('No se pudo cargar'));
  }, [id]);
  useEffect(() => { cargar(); api.get('/agency/clientes').then(r => setClientes(r.data)).catch(() => {}); }, [cargar]);
  if (!p) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const totalItems = (d.items || []).reduce((a, i) => a + Number(i.monto || 0), 0);
  const totalPlan = (d.plan_pago || []).reduce((a, i) => a + Number(i.monto || 0), 0);
  const estado = ESTADO_PROPUESTA[p.estado] || {};

  const guardar = async (extra = {}) => {
    setGuardando(true);
    try {
      await api.put('/agency/propuestas/' + id, {
        titulo: p.titulo, cliente_id: p.cliente_id || '', vigencia_dias: p.vigencia_dias,
        monto: p.monto !== null && p.monto !== '' ? p.monto : totalItems, datos: d, ...extra,
      });
      toast.success('Guardado');
      cargar();
    } catch { toast.error('No se pudo guardar'); } finally { setGuardando(false); }
  };

  const redactar = async () => {
    if (!brief.trim()) return toast.error('Contame primero qué necesita el cliente');
    setRedactando(true);
    try {
      const { data } = await api.post('/agency/propuestas/redactar', {
        brief, cliente: p.cliente, presupuesto: p.monto || null,
        servicios: (d.items || []).map(i => i.titulo).filter(Boolean),
      });
      const s = data.datos || {};
      setD(prev => ({ ...prev, ...s, plan_pago: prev.plan_pago, email_contacto: prev.email_contacto }));
      if (s.titulo && !p.titulo) setP({ ...p, titulo: s.titulo });
      toast.success('Listo, revisá el borrador y ajustá lo que quieras');
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo redactar'); } finally { setRedactando(false); }
  };

  const enviar = async () => {
    await guardar();
    try {
      const { data } = await api.post(`/agency/propuestas/${id}/enviar`);
      toast.success('Enviada a ' + data.destinos.join(', '), { duration: 6000 });
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo enviar'); }
  };

  const confirmar = async () => {
    try {
      const { data } = await api.post(`/agency/propuestas/${id}/confirmar`, { aceptada_por: 'Confirmada por vos' });
      toast.success(`Proyecto creado · ${data.cobros.length} cobros y ${data.contratos.length} contratos`, { duration: 7000 });
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo confirmar'); }
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(p.link).then(() => toast.success('Enlace copiado')).catch(() => toast.error('No se pudo copiar'));
  };

  const setItem = (i, campo, v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, [campo]: v } : x)) });
  const setCuota = (i, campo, v) => setD({ ...d, plan_pago: d.plan_pago.map((x, j) => (j === i ? { ...x, [campo]: v } : x)) });
  const card = { marginBottom: 18 };
  const sub = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 };

  return (
    <div style={{ maxWidth: 860 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/propuestas')} style={{ marginBottom: 12 }}><ArrowLeft size={15} /> Propuestas</button>

      <div className="card" style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px' }}>
          <input value={p.titulo} onChange={e => setP({ ...p, titulo: e.target.value })} style={{ fontSize: 18, fontWeight: 700 }} placeholder="Título de la propuesta" />
        </div>
        <Badge label={estado.label} color={estado.color} bg={estado.bg} />
        {p.vista_at && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>La vio el {fecha(p.vista_at)}</span>}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => guardar()} disabled={guardando}><Save size={15} /> Guardar</button>
          {p.link && <button className="btn btn-secondary btn-sm" onClick={copiarLink}><Link2 size={15} /> Copiar enlace</button>}
          {p.link && <a className="btn btn-secondary btn-sm" href={p.link} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /> Ver</a>}
          <button className="btn btn-primary btn-sm" onClick={enviar}><Send size={15} /> Enviar al cliente</button>
        </div>
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}><Sparkles size={15} /> Redacción asistida</h3>
        <div style={sub}>Contá con tus palabras qué necesita el cliente, qué charlaron y los precios que tenés en mente. El borrador sale en segundos y después lo ajustás.</div>
        <textarea rows={4} value={brief} onChange={e => setBrief(e.target.value)} style={{ fontFamily: 'inherit', fontSize: 13 }}
          placeholder="Ej: Distribuidora de alimentos en Rafaela. Quieren sitio web con catálogo y pedidos por WhatsApp, más Google Ads. Presupuesto de $1.200.000 el sitio y abono de $350.000 por mes para las campañas." />
        <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={redactar} disabled={redactando}>
          <Sparkles size={15} /> {redactando ? 'Redactando...' : 'Redactar borrador'}
        </button>
      </div>

      <div className="card" style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="form-group"><label>Cliente</label>
            <select value={p.cliente_id || ''} onChange={e => setP({ ...p, cliente_id: e.target.value })}>
              <option value="">Sin asignar</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Email para enviarla</label>
            <input value={d.email_contacto || ''} onChange={e => setD({ ...d, email_contacto: e.target.value })} placeholder="Si es distinto al del cliente" />
          </div>
          <div className="form-group"><label>Vigencia (días)</label>
            <input type="number" min="1" value={p.vigencia_dias || 30} onChange={e => setP({ ...p, vigencia_dias: e.target.value })} />
          </div>
          <div className="form-group"><label>Monto total</label>
            <input type="number" value={p.monto ?? ''} onChange={e => setP({ ...p, monto: e.target.value })} placeholder={String(totalItems)} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Los ítems suman {money(totalItems)}</div>
          </div>
        </div>
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Presentación</h3>
        <div className="form-group"><label>Introducción</label>
          <textarea rows={4} value={d.intro} onChange={e => setD({ ...d, intro: e.target.value })} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
        <div className="form-group"><label>Objetivos</label>
          <Lista valores={d.objetivos || []} onChange={v => setD({ ...d, objetivos: v })} placeholder="Duplicar las consultas por WhatsApp" /></div>
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Qué incluye la propuesta</h3>
        <div style={sub}>Los ítems marcados como abono o renovación anual se convierten en contratos cuando el cliente acepta.</div>
        {(d.items || []).map((it, i) => (
          <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input value={it.titulo || ''} onChange={e => setItem(i, 'titulo', e.target.value)} placeholder="Sitio web autoadministrable" style={{ fontWeight: 600 }} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setD({ ...d, items: d.items.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
            </div>
            <textarea rows={2} value={it.detalle || ''} onChange={e => setItem(i, 'detalle', e.target.value)} placeholder="Qué incluye en concreto" style={{ fontFamily: 'inherit', fontSize: 13, marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" value={it.monto ?? ''} onChange={e => setItem(i, 'monto', e.target.value)} placeholder="Monto" style={{ maxWidth: 160 }} />
              <select value={it.tipo || 'unico'} onChange={e => setItem(i, 'tipo', e.target.value)} style={{ maxWidth: 200 }}>
                <option value="unico">Pago único</option><option value="proyecto">Proyecto</option>
                <option value="abono">Abono mensual</option><option value="anual">Renovación anual</option>
              </select>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setD({ ...d, items: [...(d.items || []), { titulo: '', detalle: '', monto: '', tipo: 'unico' }] })}><Plus size={14} /> Agregar ítem</button>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 16 }}>
          <div className="form-group"><label>También incluye</label>
            <Lista valores={d.incluye || []} onChange={v => setD({ ...d, incluye: v })} placeholder="Capacitación de uso" /></div>
          <div className="form-group"><label>No incluye</label>
            <Lista valores={d.no_incluye || []} onChange={v => setD({ ...d, no_incluye: v })} placeholder="Producción de fotos" /></div>
        </div>
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Plan de pago</h3>
        <div style={sub}>Cuando el cliente acepta, cada línea se crea como un cobro pendiente. Suma {money(totalPlan)}.</div>
        {(d.plan_pago || []).map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <input value={c.concepto || ''} onChange={e => setCuota(i, 'concepto', e.target.value)} placeholder="Anticipo 50%" style={{ flex: '1 1 180px' }} />
            <input type="number" value={c.monto ?? ''} onChange={e => setCuota(i, 'monto', e.target.value)} placeholder="Monto" style={{ maxWidth: 150 }} />
            <input type="date" value={(c.vencimiento || '').slice(0, 10)} onChange={e => setCuota(i, 'vencimiento', e.target.value)} style={{ maxWidth: 170 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setD({ ...d, plan_pago: d.plan_pago.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setD({ ...d, plan_pago: [...(d.plan_pago || []), { concepto: '', monto: '', vencimiento: hoy() }] })}><Plus size={14} /> Agregar cuota</button>
          {totalItems > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setD({
              ...d,
              plan_pago: [{ concepto: 'Anticipo 50%', monto: Math.round(totalItems / 2), vencimiento: hoy() },
                { concepto: 'Saldo contra entrega', monto: totalItems - Math.round(totalItems / 2), vencimiento: '' }],
            })}>Dividir en anticipo y saldo</button>
          )}
        </div>
      </div>

      <div className="card" style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div className="form-group"><label>Plazo de entrega</label>
            <input value={d.plazo || ''} onChange={e => setD({ ...d, plazo: e.target.value })} placeholder="4 semanas desde el anticipo" /></div>
          <div className="form-group"><label>Condiciones</label>
            <textarea rows={3} value={d.condiciones || ''} onChange={e => setD({ ...d, condiciones: e.target.value })} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
        </div>
      </div>

      <div className="card" style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => guardar()} disabled={guardando}><Save size={15} /> Guardar cambios</button>
        {p.estado !== 'confirmada' && (
          <button className="btn btn-secondary" onClick={confirmar}><CheckCircle2 size={15} /> Marcar como aceptada</button>
        )}
        {p.estado !== 'confirmada' && p.estado !== 'no_cerro' && (
          <button className="btn btn-ghost btn-sm" onClick={() => guardar({ estado: 'no_cerro' })} style={{ color: 'var(--text-muted)' }}>No cerró</button>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          Al aceptarla se crea el proyecto, los contratos de los abonos y los cobros del plan de pago.
        </span>
      </div>
    </div>
  );
}
